import { isNumber, min } from "lodash"
import React, { useEffect, useState } from "react"
import ReactApexChart from "react-apexcharts"

const Apaexlinecolumn = ({ data, localData, selectedData, labels }) => {
  // Extracting dates, ranks, revenues, and downloads from the 'data' object
  const dates =
    selectedData == "Local" ? Object.keys(localData) : Object.keys(data) // Get the dates (keys)

  const extractData = (key, transformFn = val => val) => {
    if (selectedData == "Local") {
      if (key == "rank") {
        return dates.map(date =>
          isNumber(data[date]?.[key]) ? transformFn(data[date][key]) : null
        )
      } else {
        return dates.map(date =>
          isNumber(localData[date]?.[key])
            ? transformFn(localData[date][key])
            : null
        )
      }
    } else {
      return dates.map(date =>
        isNumber(data[date]?.[key]) ? transformFn(data[date][key]) : null
      )
    }
  }

  // Define data mappings
  const dataMapping = {
    Rank: {
      name: "Ranks",
      actual: extractData("rank", val => (val > 0 ? val : 11)),
      change: extractData("rank", val => (val > 0 ? val : 11)),
    },
    Revenue: {
      name: "Revenue",
      actual: extractData("total_revenue", val =>
        val >= 0 ? val.toFixed(0) : null
      ),
      change: extractData("revenue_change", val => val.toFixed(1)),
    },
    Downloads: {
      name: "Downloads",
      actual: extractData("total_downloads", val =>
        val >= 0 ? val.toFixed(0) : null
      ),
      change: extractData("downloads_change", val => val.toFixed(1)),
    },
  }

  // Check if we show actual or change data
  const showChange = labels.length > 1

  const getMinMax = (dataArr, paddingPercent = 0.05) => {
    const clean = dataArr.filter(val => val !== null && !isNaN(val))
    if (!clean.length) return { min: 0, max: 10 }

    const min = Math.min(...clean)
    const max = Math.max(...clean)

    if (min === max) {
      // Flat line case: widen range artificially
      return {
        min: min * 0.95,
        max: max * 1.05,
      }
    }

    const range = max - min

    let paddedMin = Math.floor(min - range * paddingPercent)
    let paddedMax = Math.ceil(max + range * paddingPercent)

    // Prevent negative min for positive datasets
    if (min >= 0 && paddedMin < 0) {
      paddedMin = 0
    }

    return {
      min: paddedMin,
      max: paddedMax,
    }
  }

  const downloadsRange = getMinMax(
    dataMapping.Downloads.actual.map(Number),
    0.1
  )
  const revenueRange = getMinMax(dataMapping.Revenue.actual.map(Number), 0.2)

  // Create series dynamically
  const series = labels
    .map(label => ({
      name: `${dataMapping[label]?.name}`,
      // data: showChange
      //   ? dataMapping[label]?.change
      //   : dataMapping[label]?.actual,
      data: dataMapping[label]?.actual,
    }))
    .filter(Boolean)

  const colorMap = {
    Ranks: "#f1b44c",
    Downloads: "#5b73e8",
    Revenue: "#34c38f",
  }

  const dashMap = {
    Ranks: 0,
    Downloads: 0,
    Revenue: 5,
  }

  const options = {
    chart: {
      toolbar: {
        show: !1,
      },
      background: 'transparent',
      foreColor: '#d1d5db',
      events: {
        legendClick: (chartContext, seriesIndex, config) => {
          // **Prevents legend from hiding series**
          config.config.series[seriesIndex].show = true
          return false
        },
      },
      zoom: {
        enabled: true,
        allowMouseWheelZoom: true,
      },
    },

    dataLabels: {
      enabled: !1,
    },
    stroke: {
      width: [3, 4, 3],
      curve: "straight",
      dashArray: series.map(s => dashMap[s.name] || 0),
    },
    colors: series.map(s => colorMap[s.name] || "#000000"), // fallback to black
    xaxis: {
      categories: dates,
    },
    // yaxis: {
    //   // tickAmount: 9,
    //   max: Math.max(...series.flatMap(s => s.data)) + 1, // Adds space to prevent crowding
    //   tickAmount: Math.min(7, new Set(series.flatMap(s => s.data)).size),
    //   forceNiceScale: true,
    //   // min: 1,
    //   title: {
    //     text: labels.length < 2 ? "Value" : "% Change",
    //   },
    //   labels: {
    //     formatter: function (val) {
    //       if (labels.includes("Rank")) {
    //         const absVal = Math.abs(val) // Get absolute value to format correctly
    //         let formattedVal = val.toFixed(0) // Default to two decimal places

    //         if (absVal >= 1000000) {
    //           formattedVal = (val / 1000000).toFixed(0) + "M"
    //         } else if (absVal >= 1000) {
    //           formattedVal = (val / 1000).toFixed(0) + "K"
    //         }

    //         return formattedVal
    //         //return Math.round(val).toFixed(0) // Show whole numbers for rank
    //       } else {
    //         const absVal = Math.abs(val) // Get absolute value to format correctly
    //         let formattedVal = val.toFixed(1) // Default to two decimal places

    //         if (absVal >= 1000000) {
    //           formattedVal = (val / 1000000).toFixed(1) + "M"
    //         } else if (absVal >= 1000) {
    //           formattedVal = (val / 1000).toFixed(1) + "K"
    //         }

    //         return formattedVal // Preserve negative sign if applicable
    //       }
    //     },
    //   },
    // },

    yaxis: labels.map(label => {
      switch (label) {
        case "Rank":
          return {
            seriesName: "Ranks",
            title: {
              text: "Rank",
            },

            labels: {
              formatter: val => {
                if (val > 10) {
                  return "> 10"
                } else {
                  return Math.round(val)
                }
              },
            },
            // reversed: true,
          }
        case "Downloads":
          return {
            seriesName: "Downloads",
            opposite: labels.length < 2 ? false : true,
            title: {
              text: "Downloads",
            },
            min: downloadsRange.min,
            forceNiceScale: true,
            labels: {
              formatter: val => {
                // if (labels.length < 2) {
                //   return val >= 1000 ? (val / 1000).toFixed(0) + "k" : val
                // } else {
                //   return val.toFixed(2)
                // }
                if (val >= 1000000) return (val / 1000000).toFixed(1) + "M"
                if (val >= 1000) return (val / 1000).toFixed(1) + "k"
                return val.toFixed(0)
                // return val >= 1000 ? (val / 1000).toFixed(0) + "k" : val
              },
            },
          }
        case "Revenue":
          return {
            seriesName: "Revenue",
            opposite: labels.length < 2 ? false : true,
            showAlways: true,
            title: {
              //text: labels.length < 2 ? "Revenue" : " Revenue (Change)",
              text: "Revenue",
            },
            min: revenueRange.min,
            forceNiceScale: true,
            labels: {
              formatter: val => {
                // if (labels.length < 2) {
                //   return val >= 1000 ? (val / 1000).toFixed(0) + "k" : val
                // } else {
                //   return val.toFixed(2)
                // }
                if (val >= 1000000) return (val / 1000000).toFixed(1) + "M"
                if (val >= 1000) return (val / 1000).toFixed(1) + "k"
                return val.toFixed(0)
                // return val >= 1000 ? (val / 1000).toFixed(0) + "k" : val
              },
            },
            offsetX: 60,
          }
        default:
          return {}
      }
    }),

    grid: {
      borderColor: "#f1f1f1",
    },
    fill: {
      opacity: 1,
    },
    tooltip: {
      y: {
        formatter: function (val, { seriesIndex }) {
          const label = labels[seriesIndex]

          const absVal = Math.abs(val) // Get absolute value to format correctly
          let formattedVal = val // Default to two decimal places

          if (absVal >= 1000000) {
            formattedVal = (val / 1000000).toFixed(2) + "M"
          } else if (absVal >= 1000) {
            formattedVal = (val / 1000).toFixed(2) + "K"
          }

          if (labels.length == 1) {
            if (label === "Revenue") {
              return `$ ${formattedVal.toLocaleString()}`
            } else if (label === "Downloads") {
              return `${formattedVal.toLocaleString()} Downloads`
            } else if (label === "Rank") {
              if (val == 11) return ">10"
              return `# ${formattedVal}`
            }
          } else if (labels.includes("Rank")) {
            if (label === "Revenue" || label === "Downloads") {
              return `${formattedVal?.toLocaleString()} `
            } else if (label === "Rank") {
              if (val == 11) {
                return ` >10`
              } else return "#" + formattedVal
            }
          } else {
            return formattedVal + " %"
          }
          return "" + formattedVal
        },
      },
    },
    legend: {
      show: true,
      onItemClick: { toggleDataSeries: false },
    },
    
  }

  if (typeof data !== "object" || Object.keys(data).length === 0) {
    return <div>No data available</div> // Show a fallback message if data is not valid
  }
  return (
    <ReactApexChart
      options={options}
      series={series}
      // series={chartSeries}
      type="line"
      height={350}
      className="apex-charts"
    />
  )
}

export default Apaexlinecolumn
